<template>
    <Dialog v-model:open="opened">
        <DialogTrigger>
            <slot />
        </DialogTrigger>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{{
                    integration
                        ? $t("integrations.webhook.modal.title_edit")
                        : $t("integrations.webhook.modal.title_add")
                }}</DialogTitle>
                <DialogDescription>
                    {{ $t("integrations.webhook.modal.description") }}
                </DialogDescription>
            </DialogHeader>

            <div class="px-6">
                <div class="mt-4 gap-2 grid">
                    <Label for="name">{{ $t("title") }}</Label>
                    <Input v-model="form.name" id="name" />
                </div>

                <div class="mt-4 gap-2 grid">
                    <Label for="url">URL</Label>
                    <Input
                        v-model="form.url"
                        id="url"
                        placeholder="https://..."
                    />
                </div>

                <div class="mt-4 gap-2 grid">
                    <Label for="method">{{
                        $t("integrations.webhook.modal.method")
                    }}</Label>
                    <Select v-model="form.method">
                        <SelectTrigger class="w-full">
                            <SelectValue placeholder="POST" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectItem value="POST">POST</SelectItem>
                                <SelectItem value="PUT">PUT</SelectItem>
                                <SelectItem value="PATCH">PATCH</SelectItem>
                                <SelectItem value="DELETE">DELETE</SelectItem>
                                <SelectItem value="GET">GET</SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                </div>

                <div class="mt-4 gap-2 grid">
                    <Label for="secret">{{
                        $t("integrations.webhook.modal.secret")
                    }}</Label>
                    <Input
                        v-model="form.secret"
                        id="secret"
                        :placeholder="
                            $t('integrations.webhook.modal.secret_placeholder')
                        "
                    />
                </div>

                <div class="mt-4 gap-2 grid">
                    <Label>{{
                        $t("integrations.webhook.modal.headers")
                    }}</Label>
                    <div
                        v-for="(header, index) in form.headers"
                        :key="index"
                        class="gap-2 flex"
                    >
                        <Input
                            v-model="header.key"
                            :placeholder="
                                $t(
                                    'integrations.webhook.modal.header_key_placeholder',
                                )
                            "
                        />
                        <Input
                            v-model="header.value"
                            :placeholder="
                                $t(
                                    'integrations.webhook.modal.header_value_placeholder',
                                )
                            "
                        />
                        <Button
                            variant="outline"
                            @click="removeHeader(index)"
                            >{{ $t("remove") }}</Button
                        >
                    </div>
                    <Button variant="outline" class="mt-2" @click="addHeader">{{
                        $t("integrations.webhook.modal.add_header")
                    }}</Button>
                </div>
            </div>

            <DialogFooter
                class="w-full"
                :class="cn(removable ? '!justify-between' : 'justify-end')"
            >
                <Button
                    v-if="isRemovable"
                    @click="confirmDelete"
                    variant="destructive-ghost"
                    class="gap-1 flex"
                    size="xs"
                >
                    <Trash :size="16" />
                    {{ $t("integrations.remove_integration") }}
                </Button>

                <div class="flex-end flex">
                    <DialogClose>
                        <Button class="ml-4" variant="outline" type="button">{{
                            $t("cancel")
                        }}</Button>
                    </DialogClose>

                    <div class="relative">
                        <!-- Pro Lock Icon for Add button -->
                        <div
                            v-if="!integration?.id && !hasSubscription"
                            class="-right-1 -top-1 h-5 w-5 bg-white shadow-md absolute z-50 flex items-center justify-center rounded-full transition-all duration-150 hover:scale-110"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M3.33319 13.3332H12.6665M1.33319 2.6665L3.33319 10.6665H12.6665L14.6665 2.6665L10.6665 7.33317L7.99986 2.6665L5.33319 7.33317L1.33319 2.6665Z"
                                    stroke="url(#paint0_linear_1067_2699)"
                                    stroke-width="1.4"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                                <defs>
                                    <linearGradient
                                        id="paint0_linear_1067_2699"
                                        x1="1.00018"
                                        y1="1.99976"
                                        x2="10.1364"
                                        y2="14.5624"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop stop-color="#FF8D47" />
                                        <stop
                                            offset="0.329612"
                                            stop-color="#FF8947"
                                        />
                                        <stop
                                            offset="0.534043"
                                            stop-color="#FF4247"
                                        />
                                        <stop
                                            offset="0.68692"
                                            stop-color="#E92A5B"
                                        />
                                        <stop
                                            offset="0.828171"
                                            stop-color="#DD1C67"
                                        />
                                        <stop offset="1" stop-color="#CF0283" />
                                    </linearGradient>
                                </defs>
                            </svg>
                        </div>
                        <Button class="ml-4" type="button" @click="save">{{
                            integration ? $t("save_changes") : $t("add")
                        }}</Button>
                    </div>
                </div>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { cn } from "@/lib/utils";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash } from "lucide-vue-next";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
    DialogClose,
} from "@/components/ui/dialog-v2";
import {
    Select,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectGroup,
    SelectItem,
} from "@/components/ui/select";
import { useHasSubscription } from "@/composables/useHasSubscription";
import useProDialog from "@/ProModal/composables/useProDialog";
import useConfirm from "@/Pages/Zaptime/App/ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";

interface Integration {
    id?: number;
    name: string;
    data?: any;
}

const props = defineProps<{
    integration?: Integration | null;
    removable?: boolean;
}>();

const emit = defineEmits<{
    integrationCreated: [];
    integrationUpdated: [];
    integrationDeleted: [];
}>();
const isRemovable = computed(() => props.removable ?? !!props.integration?.id);
const opened = ref(false);
const { hasSubscription } = useHasSubscription();
const proDialog = useProDialog();
const { show: showConfirm } = useConfirm();
const { t } = useI18n();

const form = ref({
    name: props.integration?.name ?? "",
    url: props.integration?.data?.url ?? "",
    method: props.integration?.data?.method ?? "POST",
    secret: props.integration?.data?.secret ?? "",
    headers: (props.integration?.data?.headers ?? []).map((h: any) => ({
        key: Object.keys(h)[0],
        value: Object.values(h)[0],
    })),
});

function addHeader() {
    form.value.headers.push({ key: "", value: "" });
}
function removeHeader(index: number) {
    form.value.headers.splice(index, 1);
}

async function save() {
    // Check if user is trying to create a new webhook integration without subscription
    if (!props.integration?.id && !hasSubscription) {
        opened.value = false; // Close the webhook dialog first
        await new Promise((resolve) => setTimeout(resolve, 150)); // Small delay for animation
        proDialog.show({
            feature: "integrations",
        });
        return;
    }

    const headersObject: Record<string, string> = {};
    for (const h of form.value.headers) {
        if (h.key) headersObject[h.key] = h.value ?? "";
    }

    const payload = {
        name: form.value.name,
        url: form.value.url,
        method: form.value.method,
        secret: form.value.secret || undefined,
        headers: headersObject,
    };

    if (props.integration?.id) {
        router.put(
            route("app.webhook-integrations.update", {
                integration: props.integration.id,
            }),
            payload,
            {
                onSuccess: () => {
                    emit("integrationUpdated");
                    opened.value = false;
                },
                onFinish: () => {
                    opened.value = false;
                },
            },
        );
    } else {
        router.post(route("app.webhook-integrations.store"), payload, {
            onSuccess: () => {
                emit("integrationCreated");
                opened.value = false;
            },
            onFinish: () => {
                opened.value = false;
            },
        });
    }
}

async function confirmDelete() {
    const confirmed = await showConfirm({
        header: t("integrations.webhook.modal.delete_title"),
        message: t("integrations.webhook.modal.delete_description"),
        confirm: t("remove"),
        cancel: t("cancel"),
    });

    if (confirmed) {
        remove();
    }
}

function remove() {
    if (!props.integration?.id) return;
    router.delete(
        route("app.webhook-integrations.delete", {
            integration: props.integration.id,
        }),
        {
            onSuccess: () => {
                emit("integrationDeleted");
                opened.value = false;
            },
            onFinish: () => {
                opened.value = false;
            },
        },
    );
}
</script>
